import { messageHasPrototyperArtifact, resolvePrototypeName } from '@ideoz/core';
import type { Message } from '@ideoz/core';
import { versionHasNumber, type TurnVersionsLookup } from './turnVersions';

/**
 * The panel title's menu: the conversation's prototypes and their versions, so the founder can
 * open any of them without scrolling the chat to its card. Built from what the pills read (the
 * turn messages and the versions), so the menu and the pills always agree.
 */

export type VersionMenuEntryState = 'finished' | 'unfinished' | 'building';

export interface VersionMenuEntry {
  llmInteractionId: string;
  /** Passed to the same open handler a pill click uses. */
  demoLinkId?: string | number;
  /** "V4", "Didn't finish", "Building…". */
  label: string;
  state: VersionMenuEntryState;
  createdAt?: string;
  isCurrent: boolean;
  isPublished: boolean;
}

export interface VersionMenuGroup {
  key: string;
  name: string;
  /** Newest first. */
  entries: VersionMenuEntry[];
}

/** Versions a turn no longer has on screen anywhere: "Undo this change", or replaced. */
const HIDDEN_STATUSES = new Set(['discarded', 'superseded']);

export function buildPrototypeVersionMenu(input: {
  messages: readonly Message[];
  versions?: TurnVersionsLookup;
  /** The version on screen. */
  activeTurnId?: string | null;
  /** The turn building right now. */
  liveTurnId?: string | null;
  /** Version ids with a published link. */
  publishedVersionIds?: ReadonlySet<string>;
}): VersionMenuGroup[] {
  const { messages, versions, activeTurnId, liveTurnId, publishedVersionIds } = input;
  const ready = versions?.status === 'ready';
  const groups = new Map<string, VersionMenuGroup>();
  let sawUser = false;

  for (const m of messages) {
    if (m.role === 'user') {
      sawUser = true;
      continue;
    }
    // As the pills: a prototyper turn with a user turn before it (a scope's greeting is not one).
    if (!sawUser || !m.llmInteractionId || !messageHasPrototyperArtifact(m)) continue;
    const lid = m.llmInteractionId;
    const version = ready ? versions!.get(lid) : undefined;
    if (version && HIDDEN_STATUSES.has(version.status)) continue;

    const name =
      resolvePrototypeName({
        prototypeTitle: m.prototypeTitle,
        prototypeDisplayName: m.prototypeDisplayName,
        prototypeKind: m.prototypeKind,
      }) ?? 'Prototype';
    const key = version?.prototypeId ?? `name:${name}`;

    const number = version?.number ?? (ready ? undefined : (m.prototypeVersionIndex ?? undefined));
    const settled = ready ? versionHasNumber(version) : m.buildStatus === 'success' || number != null;
    const state: VersionMenuEntryState =
      lid === liveTurnId ? 'building' : settled === false ? 'unfinished' : 'finished';
    const label =
      state === 'building'
        ? 'Building…'
        : state === 'unfinished'
          ? "Didn't finish"
          : typeof number === 'number' && number > 0
            ? `V${number}`
            : 'Version';

    let group = groups.get(key);
    if (!group) {
      group = { key, name, entries: [] };
      groups.set(key, group);
    }
    group.name = name;
    group.entries.unshift({
      llmInteractionId: lid,
      demoLinkId: m.prototyper_demo_link_id ?? undefined,
      label,
      state,
      createdAt: version?.createdAt ?? m.createdAt,
      isCurrent: lid === activeTurnId,
      isPublished: Boolean(version && publishedVersionIds?.has(version.id)),
    });
  }

  // The prototype on screen first, then the rest newest first.
  const list = [...groups.values()];
  const newest = (g: VersionMenuGroup): number => {
    const t = g.entries[0]?.createdAt ? new Date(g.entries[0].createdAt).getTime() : 0;
    return Number.isNaN(t) ? 0 : t;
  };
  list.sort((a, b) => {
    const aCur = a.entries.some((e) => e.isCurrent) ? 1 : 0;
    const bCur = b.entries.some((e) => e.isCurrent) ? 1 : 0;
    if (aCur !== bCur) return bCur - aCur;
    return newest(b) - newest(a);
  });
  return list;
}

/** "just now", "5 min ago", "3 h ago", "2 d ago". */
export function relativeTime(iso: string | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const minutes = Math.floor(Math.max(0, now - t) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}
